import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import { normalizeRoleName } from '../lib/roles';
import { resolveProfile, userLanding, isPlatformAdmin } from '../lib/profileAccess';
import { NoAccessPage } from '../components/auth/ProfileRouteGuard';
import { DashboardPage } from '../pages/dashboard/DashboardPage';
import { ErrorBoundary } from '../components/ErrorBoundary';

export function RoleAwareHome() {
  const { user } = useAuthStore();
  return <Navigate to={userLanding(user)} replace />;
}
export function RoleAwareDashboard() {
  const { user } = useAuthStore();
  const landing = userLanding(user);
  return landing === '/dashboard' ? <DashboardPage /> : <Navigate to={landing} replace />;
}
export function LazySpinner() {
  return <div role="status" className="flex h-full items-center justify-center py-32">Loading…</div>;
}
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuthStore();
  if (isLoading) return <LazySpinner />;
  return user ? <>{children}</> : <Navigate to="/login" replace />;
}
// The outer ProfileRouteGuard authorizes every locked profile first. Keep legacy
// role/capability restrictions for routes that predate the locked profile contract.
export function RequireRoles({ roles, children }: { roles: string[]; children: ReactNode }) {
  const { user } = useAuthStore();
  if (!user) return null;
  if (resolveProfile(user) || isPlatformAdmin(user)) return <>{children}</>;
  return roles.some(role => normalizeRoleName(role) === normalizeRoleName(user.role?.name)) ? <>{children}</> : <NoAccessPage />;
}
export function RequireCapability({ capability, children }: { capability: 'discountUi' | 'recruitment' | 'mockupBoard'; children: ReactNode }) {
  const { user, tenant } = useAuthStore();
  if (isPlatformAdmin(user)) return <>{children}</>;
  // Recruitment is a tenant switch the server also enforces (recruit-channels 404 when off),
  // so a locked profile (HR) must NOT bypass it: openings and channel tabs have to agree.
  if (capability !== 'recruitment' && resolveProfile(user)) return <>{children}</>;
  return tenant?.capabilities?.[capability] === true ? <>{children}</> : <NoAccessPage />;
}
export function BlockReps({ children }: { children: ReactNode }) {
  const { user } = useAuthStore();
  if (!user) return null;
  if (resolveProfile(user)) return <>{children}</>;
  return ['field_executive', 'sales_executive', 'sales_exec', 'sales_rep'].includes(normalizeRoleName(user.role?.name)) ? <NoAccessPage /> : <>{children}</>;
}
export function StrictViewBlock({ children }: { children: ReactNode }) { return <>{children}</>; }
export function LogoutPage() {
  const { logout } = useAuthStore();
  const [done, setDone] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void logout().catch(() => {}).finally(() => { if (!cancelled) setDone(true); });
    return () => { cancelled = true; };
  }, [logout]);
  return done ? <Navigate to="/login" replace /> : <LazySpinner />;
}
export function RoutedErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <ErrorBoundary key={pathname} label={pathname}>{children}</ErrorBoundary>;
}
